import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo, useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  ArrowRight,
  UserCheck,
  Calendar,
  Clock,
  Sparkles,
  Share2,
  BookmarkCheck,
  ExternalLink,
  List,
  ChevronDown,
  ArrowUp,
  Check,
  Copy,
} from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { ArticleToc } from "@/components/articles";
import { fetchArticle, fetchArticles, getShareImageUrl, type Article } from "@/lib/articles";
import { parseToIsoDate, formatDisplayDate, formatReadTime } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";

const topicStopWords = new Set(
  "a an and are as at be been being by can could did do does for from had has have how i in into is it its may me might more most not of on or our out per should so than that the their them then there these they this those through to under use used using versus was we were what when where which who why will with within would you your".split(
    " ",
  ),
);

function getArticleTerms(article: Article): Set<string> {
  const sectionText = (article.sections || [])
    .map((section) => `${section.heading || ""} ${section.body || ""}`)
    .join(" ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/https?:\/\/\S+/g, " ");
  const tags = Array.isArray(article.tags) ? article.tags.join(" ") : "";
  const text = [article.title, article.excerpt, article.category, tags, sectionText]
    .join(" ")
    .toLowerCase();
  return new Set(
    (text.match(/[a-z0-9]{3,}/g) || [])
      .map((term) => term.replace(/s$/, ""))
      .filter((term) => term.length > 2 && !topicStopWords.has(term)),
  );
}

function articleSimilarity(
  sourceTerms: Set<string>,
  candidateTerms: Set<string>,
  inverseDocumentFrequency: Map<string, number>,
): number {
  const sharedWeight = [...sourceTerms]
    .filter((term) => candidateTerms.has(term))
    .reduce((sum, term) => sum + (inverseDocumentFrequency.get(term) || 1), 0);
  const sourceMagnitude = Math.sqrt(
    [...sourceTerms].reduce((sum, term) => sum + (inverseDocumentFrequency.get(term) || 1) ** 2, 0),
  );
  const candidateMagnitude = Math.sqrt(
    [...candidateTerms].reduce(
      (sum, term) => sum + (inverseDocumentFrequency.get(term) || 1) ** 2,
      0,
    ),
  );
  return sourceMagnitude && candidateMagnitude
    ? sharedWeight / (sourceMagnitude * candidateMagnitude)
    : 0;
}

function trimToWordBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const truncated = value.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trim();
}

function buildMetaDescription(
  source: string,
  title: string,
  category: string,
  authorName: string,
): string {
  const cleaned = source.replace(/\s+/g, " ").trim();
  const suffix = ` Explore ${title}${category ? ` in ${category}` : ""} with practical engineering guidance from ${authorName} at BuildWithJIT.`;
  let description = cleaned.length < 120 ? `${cleaned}${suffix}` : cleaned;
  if (description.length < 120) {
    description = `Practical engineering guidance on ${title}${category ? ` in ${category}` : ""} from ${authorName} at BuildWithJIT. ${description}`;
  }
  return trimToWordBoundary(description, 160);
}

export const Route = createFileRoute("/articles/$slug")({
  loader: async ({ params }) => {
    const [article, allArticles, authorProfile] = await Promise.all([
      fetchArticle(params.slug),
      fetchArticles().catch(() => [] as Article[]),
      fetchAuthorProfile(),
    ]);
    if (!article) {
      throw notFound();
    }
    return { article, allArticles, authorProfile };
  },
  head: ({ loaderData }) => {
    const art = loaderData?.article;
    const authorProfile = loaderData?.authorProfile;
    if (!art) {
      return {
        meta: [
          { title: "404 - Article Not Found | BuildWithJIT" },
          {
            name: "description",
            content: "The requested article could not be found or has been moved.",
          },
          { name: "robots", content: "noindex, nofollow" },
          { name: "googlebot", content: "noindex, nofollow" },
        ],
      };
    }

    const rawTitle = (art.meta_title || art.title || "Article")
      .replace(/\s*\|\s*BuildWithJIT$/i, "")
      .trim();
    const brandSuffix = " | BuildWithJIT";
    const titlePrefix = trimToWordBoundary(rawTitle, 60 - brandSuffix.length);
    const title = `${titlePrefix || rawTitle.slice(0, 60 - brandSuffix.length)}${brandSuffix}`;
    const description = buildMetaDescription(
      art.meta_description || art.excerpt || "Practical engineering insights.",
      art.title,
      art.category || "",
      authorProfile?.name ?? "the author",
    );
    const shareImg = getShareImageUrl(art.image);
    const isoDate = parseToIsoDate(art.date);
    const canonicalUrl = `https://buildwithjit.com/articles/${art.slug}`;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "robots",
          content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        {
          name: "googlebot",
          content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        { property: "og:site_name", content: "BuildWithJIT" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:image", content: shareImg.url },
        { property: "og:image:secure_url", content: shareImg.url },
        { property: "og:image:type", content: shareImg.type },
        { property: "og:image:width", content: String(shareImg.width) },
        { property: "og:image:height", content: String(shareImg.height) },
        { property: "og:image:alt", content: title },
        { property: "article:author", content: authorProfile?.name ?? "BuildWithJIT author" },
        { property: "article:published_time", content: isoDate },
        ...(art.updated_at
          ? [{ property: "article:modified_time", content: parseToIsoDate(art.updated_at) }]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@buildwithjit" },
        { name: "twitter:creator", content: "@buildwithjit" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: shareImg.url },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "icon", href: "/favicon.ico", sizes: "any" },
        { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
        { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
        { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
        { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "shortcut icon", href: "/favicon.ico" },
        { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      ],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const data = Route.useLoaderData();
  const article = data.article;
  const rawArticles = data.allArticles;
  const authorProfile = data.authorProfile;
  const social = getAuthorSocial(authorProfile);

  const [activeId, setActiveId] = useState<string>("");
  const [readingProgress, setReadingProgress] = useState<number>(0);
  const [copied, setCopied] = useState<boolean>(false);
  const tocNavRef = useRef<HTMLElement>(null);

  // Keep related links within the same topic category.
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    const articles = (rawArticles || []).filter((candidate) => candidate.slug !== article.slug);
    const corpus = [article, ...articles];
    const articleTermSets = corpus.map((candidate) => getArticleTerms(candidate));
    const documentFrequency = new Map<string, number>();
    for (const terms of articleTermSets) {
      for (const term of terms) {
        documentFrequency.set(term, (documentFrequency.get(term) || 0) + 1);
      }
    }

    const inverseDocumentFrequency = new Map(
      [...documentFrequency].map(([term, frequency]) => [
        term,
        Math.log((corpus.length + 1) / (frequency + 1)) + 1,
      ]),
    );
    const sourceTerms = articleTermSets[0] || new Set<string>();
    const category = (article.category || "").trim().toLowerCase();
    return articles
      .map((candidate, index) => {
        const sameCategory =
          !!category && (candidate.category || "").trim().toLowerCase() === category;
        const candidateTerms = articleTermSets[index + 1] || new Set<string>();
        const score =
          articleSimilarity(sourceTerms, candidateTerms, inverseDocumentFrequency) +
          (sameCategory ? 0.08 : 0);
        return { article: candidate, score };
      })
      .filter(({ score }) => score >= 0.09)
      .sort((left, right) => right.score - left.score)
      .slice(0, 5)
      .map(({ article: related }) => related);
  }, [rawArticles, article]);

  // Dynamically extract FAQ schema if Q&A items exist in body
  const faqSchema = useMemo(() => {
    if (!article?.sections) return null;
    const qaPairs: { question: string; answer: string }[] = [];

    for (const section of article.sections) {
      const text = section.body || "";
      const lines = text.split("\n");
      let currentQ = "";
      let currentA = "";
      for (const line of lines) {
        const qMatch = line.match(/^###?\s*(.+?\?)\s*$/) || line.match(/^\*\*(.+?\?)\*\*\s*$/);
        const questionText = qMatch?.[1];
        if (questionText) {
          if (currentQ && currentA.trim()) {
            qaPairs.push({ question: currentQ, answer: currentA.trim().replace(/[*_#`]/g, "") });
          }
          currentQ = questionText.replace(/[*_#`]/g, "").trim();
          currentA = "";
        } else if (currentQ) {
          if (line.startsWith("#")) {
            if (currentQ && currentA.trim()) {
              qaPairs.push({ question: currentQ, answer: currentA.trim().replace(/[*_#`]/g, "") });
            }
            currentQ = "";
            currentA = "";
          } else {
            currentA += line + " ";
          }
        }
      }
      if (currentQ && currentA.trim()) {
        qaPairs.push({ question: currentQ, answer: currentA.trim().replace(/[*_#`]/g, "") });
      }
    }

    if (qaPairs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: qaPairs.map((pair) => ({
        "@type": "Question",
        name: pair.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: pair.answer.slice(0, 500),
        },
      })),
    };
  }, [article]);

  // Extract Table of Contents from headings
  const tocItems = useMemo(() => {
    if (!article?.sections) return [];
    const items: { title: string; id: string; level: number }[] = [];
    article.sections.forEach((s) => {
      if (s.heading && s.heading.trim()) {
        const title = s.heading.trim();
        const id = title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        items.push({ title, id, level: 2 });
      }
      if (s.body) {
        const lines = s.body.split("\n");
        for (const line of lines) {
          const match = line.match(/^(#{2,3})\s+(.+)$/);
          if (match && match[1] && match[2]) {
            const level = match[1].length;
            const title = match[2].trim().replace(/\*\*/g, "");
            // Filter out FAQ question entries (ending with '?') to keep TOC concise and focused on main sections
            if (level === 3 && title.endsWith("?")) {
              continue;
            }
            // Skip title that duplicates the main article title
            if (title.toLowerCase() === (article.title || "").toLowerCase()) {
              continue;
            }
            const id = title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            if (!items.some((i) => i.id === id)) {
              items.push({ title, id, level });
            }
          }
        }
      }
    });
    return items;
  }, [article?.sections, article?.title]);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const currentProgress = Math.min(
          100,
          Math.max(0, Math.round((window.scrollY / totalHeight) * 100)),
        );
        setReadingProgress(currentProgress);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (tocItems.length === 0) return;
    const headingElements = tocItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (headingElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) {
          setActiveId(visible.target.id);
        }
      },
      { rootMargin: "-90px 0px -65% 0px", threshold: 0.1 },
    );

    headingElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [tocItems]);

  // Smoothly keep the active heading visible inside the TOC sidebar navigation without clipping
  useEffect(() => {
    if (!activeId || !tocNavRef.current) return;
    const activeLink = tocNavRef.current.querySelector<HTMLAnchorElement>(`a[href="#${activeId}"]`);
    if (activeLink) {
      activeLink.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }, [activeId]);

  if (!article) {
    return (
      <SiteShell social={social}>
        <div className="container-wide py-24 text-center max-w-xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-wider text-primary mb-2">
            404 NOT FOUND
          </div>
          <h1 className="text-4xl font-bold mb-4 text-foreground">Article Not Found</h1>
          <p className="text-muted-foreground mb-8 text-base">
            The article you are looking for does not exist, has been unpublished, or the URL may be
            incorrect.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft size={16} /> Explore all articles
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-xl font-medium hover:bg-muted transition-colors"
            >
              Go to Home
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  const isoDate = parseToIsoDate(article.date);
  const modifiedDate = article.updated_at ? parseToIsoDate(article.updated_at) : undefined;
  const canonicalUrl = `https://buildwithjit.com/articles/${article.slug}`;
  const shareImg = getShareImageUrl(article.image);

  // Structured Data JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt || article.title,
    image: shareImg.url,
    inLanguage: "en",
    isAccessibleForFree: true,
    ...(article.tags?.length
      ? { keywords: article.tags.map((tag) => tag.trim()).filter(Boolean) }
      : {}),
    datePublished: isoDate,
    ...(modifiedDate ? { dateModified: modifiedDate } : {}),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", ".article-excerpt"],
    },
    author: {
      "@type": "Person",
      name: authorProfile.name,
      jobTitle: authorProfile.job_title,
      url: "https://buildwithjit.com/author/jignesh-mevada",
      sameAs: Object.values(social),
    },
    publisher: {
      "@type": "Organization",
      name: "BuildWithJIT",
      url: "https://buildwithjit.com",
      sameAs: Object.values(social),
      logo: {
        "@type": "ImageObject",
        url: "https://buildwithjit.com/icon-512.png",
        width: 512,
        height: 512,
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://buildwithjit.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Articles",
        item: "https://buildwithjit.com/articles",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: canonicalUrl,
      },
    ],
  };

  const handleShare = (platform?: "whatsapp" | "twitter" | "linkedin") => {
    const text = `${article.title}\n${canonicalUrl}`;
    if (!platform) {
      if (typeof navigator !== "undefined" && navigator.share) {
        navigator
          .share({
            title: article.title,
            text: article.excerpt || article.title,
            url: canonicalUrl,
          })
          .catch(() => {});
        return;
      }
      navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    if (platform === "whatsapp") {
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
        "_blank",
        "noopener,noreferrer",
      );
    } else if (platform === "twitter") {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(canonicalUrl)}`,
        "_blank",
        "noopener,noreferrer",
      );
    } else if (platform === "linkedin") {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -90;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveId(id);
      if (window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteShell social={social}>
      {/* Inject Article & Breadcrumb JSON-LD schemas */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <article className="article-page w-full max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 md:py-12">
        {/* Navigation Breadcrumb (Full Width) */}
        <div className="mb-8 flex items-center justify-between text-[13.5px] text-muted-foreground border-b border-border/40 pb-4">
          <Link
            to="/articles"
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors font-medium"
          >
            <ArrowLeft size={16} /> All articles
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/articles"
              search={{ category: article.category }}
              className="font-mono text-[12.5px] uppercase tracking-wider text-primary hover:underline"
            >
              {article.category}
            </Link>
            <span className="text-muted-foreground/40">•</span>
            <span className="font-mono text-[12.5px] text-muted-foreground flex items-center gap-1">
              <Clock size={13} className="text-primary/70" /> {article.read}
            </span>
          </div>
        </div>

        {/* 2-Column Responsive Layout: Left (Sticky TOC Navigation) + Right (Full Reading Flow) */}
        <div className="flex flex-col lg:flex-row items-start gap-8 xl:gap-12 w-full">
          {/* Left Column: Sticky Sidebar Table of Contents */}
          <ArticleToc
            tocItems={tocItems}
            activeId={activeId}
            readingProgress={readingProgress}
            readTime={article.read}
            tocNavRef={tocNavRef}
            handleScrollTo={handleScrollTo}
            scrollToTop={scrollToTop}
          />

          {/* Right Column: Full Reading Flow */}
          <div className="flex-1 min-w-0 w-full max-w-5xl 2xl:max-w-6xl">
            {/* Article Head */}
            <header className="article-head !max-w-none !m-0 !text-left mb-8">
              {/* Visible Breadcrumbs */}
              <nav aria-label="Breadcrumb" className="mb-4">
                <ol className="flex items-center gap-2 text-xs text-muted-foreground font-mono flex-wrap">
                  <li>
                    <Link to="/" className="hover:text-primary transition-colors">
                      Home
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li>
                    <Link to="/articles" className="hover:text-primary transition-colors">
                      Articles
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li
                    className="text-foreground font-medium truncate max-w-[240px] sm:max-w-md"
                    aria-current="page"
                  >
                    {article.title}
                  </li>
                </ol>
              </nav>

              <div className="section-kicker font-mono text-[13.5px] uppercase tracking-widest text-primary mb-3">
                {article.category.toUpperCase()} / EDITORIAL ARCHITECTURE
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-4">
                {article.title}
              </h1>
              {article.excerpt && article.excerpt.trim() ? (
                <p className="article-excerpt text-[19.5px] md:text-[21px] text-muted-foreground leading-relaxed mb-6 font-normal">
                  {article.excerpt}
                </p>
              ) : null}

              {/* Author Byline & Trust Signals */}
              <div className="flex flex-wrap items-center gap-y-3 gap-x-6 py-4 border-y border-border/70 text-[14.5px] text-muted-foreground">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                    JM
                  </div>
                  <div>
                    <Link
                      to="/author/jignesh-mevada"
                      className="font-semibold text-foreground flex items-center gap-1.5 hover:text-primary transition-colors"
                    >
                      {authorProfile.name}
                      <span className="text-[11px] font-mono uppercase bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                        Author
                      </span>
                    </Link>
                    <div className="text-[13px] text-muted-foreground">
                      Founder &amp; AI Systems Architect
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-[13.5px] font-mono ml-auto flex-wrap">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} className="text-primary/70" /> Published{" "}
                    {formatDisplayDate(article.date)}
                  </span>
                  <span className="text-muted-foreground/40">•</span>
                  <span className="flex items-center gap-1 text-primary font-medium">
                    Last updated {formatDisplayDate(article.date)}
                  </span>
                  <span className="text-muted-foreground/40">•</span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} className="text-primary/70" /> {formatReadTime(article.read)}
                  </span>
                </div>
              </div>
            </header>

            {/* Interactive Social Share Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 mb-8 rounded-xl border border-border/80 bg-card/60 backdrop-blur-sm text-sm">
              <div className="flex items-center gap-2 text-muted-foreground font-medium text-xs sm:text-sm">
                <Share2 size={15} className="text-primary" />
                <span>Share this article:</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleShare("whatsapp")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 transition-colors cursor-pointer"
                  title="Share on WhatsApp"
                >
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => handleShare("linkedin")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0A66C2]/15 text-[#0A66C2] hover:bg-[#0A66C2]/25 transition-colors cursor-pointer"
                  title="Share on LinkedIn"
                >
                  LinkedIn
                </button>
                <button
                  type="button"
                  onClick={() => handleShare("twitter")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-foreground/10 text-foreground hover:bg-foreground/20 transition-colors cursor-pointer"
                  title="Share on X (Twitter)"
                >
                  X (Twitter)
                </button>
                <button
                  type="button"
                  onClick={() => handleShare()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                  title="Copy link or share via device"
                >
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  <span>{copied ? "Copied Link!" : "Copy Link"}</span>
                </button>
              </div>
            </div>

            {/* Cover Image */}
            {article.image && !article.image.includes("/folders/") && (
              <div className="mb-10 rounded-2xl overflow-hidden border border-border/60 shadow-sm bg-muted">
                <img
                  className="w-full h-auto object-cover max-h-[520px]"
                  src={article.image}
                  alt={article.title}
                  width={1408}
                  height={912}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={(e) => {
                    (e.currentTarget as HTMLElement).style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Mobile/Tablet Collapsible TOC */}
            {tocItems.length >= 2 && (
              <details className="lg:hidden mb-8 rounded-2xl border border-border/80 bg-card/60 backdrop-blur-sm p-4 group">
                <summary className="flex items-center justify-between cursor-pointer font-mono text-[13.5px] uppercase tracking-wider text-foreground select-none">
                  <span className="flex items-center gap-2 font-semibold">
                    <List size={15} className="text-primary" /> Table of Contents ({tocItems.length}
                    )
                  </span>
                  <ChevronDown
                    size={16}
                    className="text-muted-foreground group-open:rotate-180 transition-transform duration-200"
                  />
                </summary>
                <nav className="mt-3 pt-3 border-t border-border/50 space-y-1.5 text-[13.5px]">
                  {tocItems.map((item, idx) => (
                    <a
                      key={idx}
                      href={`#${item.id}`}
                      onClick={(e) => handleScrollTo(e, item.id)}
                      className={`block py-1.5 transition-colors ${
                        item.level === 3
                          ? "pl-4 text-muted-foreground/80 text-[12.5px]"
                          : "font-medium text-muted-foreground"
                      } hover:text-primary ${activeId === item.id ? "text-primary font-semibold" : ""}`}
                    >
                      <span className="font-mono text-[11.5px] text-primary/60 mr-1.5">
                        {String(idx + 1).padStart(2, "0")}.
                      </span>
                      {item.title}
                    </a>
                  ))}
                </nav>
              </details>
            )}

            {/* Article Body with 1.5px Font Size Increase on Paragraphs and Headings */}
            <div className="article-body !max-w-none !my-0">
              {article.sections.map((section, index) => (
                <section key={index} className="mb-10">
                  {section.heading && (
                    <h2
                      id={section.heading
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)/g, "")}
                      className="text-2xl md:text-[32px] font-bold tracking-tight text-foreground mt-8 mb-4 scroll-mt-24"
                    >
                      {section.heading}
                    </h2>
                  )}
                  <div className="prose prose-lg dark:prose-invert max-w-none text-[17.5px] leading-[1.8] prose-p:text-[17.5px] prose-p:leading-[1.8] prose-li:text-[17.5px] prose-li:leading-[1.8] prose-headings:font-bold prose-headings:tracking-tight prose-a:text-primary hover:prose-a:underline prose-code:font-mono prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:rounded-xl prose-pre:border prose-pre:border-border prose-table:border prose-table:border-border">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ children, ...props }) => (
                          <div className="article-table-container my-8 w-full overflow-x-auto rounded-xl border border-border/80 bg-card/60 shadow-sm backdrop-blur-sm">
                            <table
                              className="w-full text-left text-[14.5px] border-collapse min-w-[560px] m-0"
                              {...props}
                            >
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children, ...props }) => (
                          <thead
                            className="bg-muted/80 border-b border-border text-foreground font-mono uppercase text-[12px] tracking-wider"
                            {...props}
                          >
                            {children}
                          </thead>
                        ),
                        tbody: ({ children, ...props }) => (
                          <tbody
                            className="divide-y divide-border/50 text-foreground/90"
                            {...props}
                          >
                            {children}
                          </tbody>
                        ),
                        tr: ({ children, ...props }) => (
                          <tr
                            className="transition-colors hover:bg-muted/40 even:bg-muted/15"
                            {...props}
                          >
                            {children}
                          </tr>
                        ),
                        th: ({ children, ...props }) => (
                          <th
                            className="px-4 py-3.5 font-bold text-foreground align-middle border-b border-border font-mono text-[12.5px]"
                            {...props}
                          >
                            {children}
                          </th>
                        ),
                        td: ({ children, ...props }) => (
                          <td
                            className="px-4 py-3.5 align-top leading-relaxed text-foreground/90 border-b border-border/40 text-[14px]"
                            {...props}
                          >
                            {children}
                          </td>
                        ),
                        pre: ({ children, ...props }) => (
                          <div className="my-6 rounded-xl border border-border/80 bg-muted/40 shadow-sm overflow-hidden">
                            <pre
                              className="p-4 sm:p-5 overflow-x-auto font-mono text-[13.5px] leading-relaxed text-foreground"
                              {...props}
                            >
                              {children}
                            </pre>
                          </div>
                        ),
                        code: ({
                          inline,
                          className,
                          children,
                          ...props
                        }: React.ComponentPropsWithoutRef<"code"> & { inline?: boolean }) => {
                          if (inline) {
                            return (
                              <code
                                className="font-mono text-[0.88em] bg-muted text-primary border border-border/60 px-1.5 py-0.5 rounded font-medium break-words"
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          }
                          return (
                            <code
                              className={`font-mono text-[13.5px] ${className || ""}`}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        },
                        blockquote: ({ children, ...props }) => (
                          <blockquote
                            className="my-6 border-l-4 border-primary bg-primary/5 pl-5 pr-4 py-3.5 rounded-r-xl italic text-foreground/90 font-serif text-[18px] leading-relaxed shadow-sm"
                            {...props}
                          >
                            {children}
                          </blockquote>
                        ),
                        ul: ({ children, ...props }) => (
                          <ul
                            className="my-5 ml-6 list-disc space-y-2 text-foreground/90 text-[17.5px] leading-[1.8]"
                            {...props}
                          >
                            {children}
                          </ul>
                        ),
                        ol: ({ children, ...props }) => (
                          <ol
                            className="my-5 ml-6 list-decimal space-y-2 text-foreground/90 text-[17.5px] leading-[1.8]"
                            {...props}
                          >
                            {children}
                          </ol>
                        ),
                        hr: ({ ...props }) => <hr className="my-10 border-border/80" {...props} />,
                        img: ({ src, alt, ...props }) => (
                          <figure className="my-8 rounded-2xl overflow-hidden border border-border/80 shadow-sm bg-muted/20">
                            <img
                              src={src}
                              alt={alt || `${article.title} technical architecture diagram`}
                              width={1200}
                              height={675}
                              className="w-full h-auto max-h-[540px] object-cover"
                              loading="lazy"
                              decoding="async"
                              {...props}
                            />
                            {alt && (
                              <figcaption className="text-center text-xs text-muted-foreground p-3 font-mono border-t border-border/40 bg-muted/30">
                                {alt}
                              </figcaption>
                            )}
                          </figure>
                        ),
                        h1: ({ node, children, ...props }) => (
                          <h2
                            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-4"
                            {...props}
                          >
                            {children}
                          </h2>
                        ),
                        h2: ({ node, children, ...props }) => {
                          const text = String(children || "").replace(/\[object Object\]/g, "");
                          const id = text
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/(^-|-$)/g, "");
                          return (
                            <h2
                              id={id || undefined}
                              className="text-2xl md:text-[32px] font-bold tracking-tight text-foreground mt-10 mb-4 scroll-mt-24"
                              {...props}
                            >
                              {children}
                            </h2>
                          );
                        },
                        h3: ({ node, children, ...props }) => {
                          const text = String(children || "").replace(/\[object Object\]/g, "");
                          const id = text
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/(^-|-$)/g, "");
                          return (
                            <h3
                              id={id || undefined}
                              className="text-xl md:text-[24px] font-bold tracking-tight text-foreground mt-8 mb-3 scroll-mt-24"
                              {...props}
                            >
                              {children}
                            </h3>
                          );
                        },
                        a: ({ href, children, ...props }) => {
                          const isExternal =
                            typeof href === "string" &&
                            (href.startsWith("http://") || href.startsWith("https://"));
                          const isSafe =
                            typeof href === "string" &&
                            (href.startsWith("/") || href.startsWith("#") || isExternal);
                          const safeHref = isSafe ? href : "#";
                          return (
                            <a
                              href={safeHref}
                              {...(isExternal
                                ? { target: "_blank", rel: "noopener noreferrer" }
                                : {})}
                              {...props}
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {section.body}
                    </ReactMarkdown>
                  </div>
                </section>
              ))}
              <div className="text-center text-muted-foreground my-8">✳ ✳ ✳</div>
              {relatedArticles.length > 0 && (
                <p className="text-sm text-muted-foreground my-8">
                  Related reading:{" "}
                  {relatedArticles.map((related, index) => (
                    <span key={related.slug}>
                      {index > 0 ? " · " : ""}
                      <Link
                        to="/articles/$slug"
                        params={{ slug: related.slug }}
                        className="text-primary hover:underline"
                      >
                        {related.title}
                      </Link>
                    </span>
                  ))}
                </p>
              )}
            </div>

            {/* End of Article Share Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 my-10 rounded-2xl border border-primary/20 bg-primary/5 text-sm">
              <div className="space-y-0.5 text-center sm:text-left">
                <div className="font-bold text-foreground flex items-center justify-center sm:justify-start gap-2 text-base">
                  <Share2 size={18} className="text-primary" />
                  <span>Found this architectural guide valuable?</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share it with your engineering team or network.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <button
                  type="button"
                  onClick={() => handleShare("whatsapp")}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#25D366] text-white hover:bg-[#25D366]/90 transition-transform active:scale-95 cursor-pointer shadow-sm"
                  title="Share on WhatsApp"
                >
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => handleShare("linkedin")}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#0A66C2] text-white hover:bg-[#0A66C2]/90 transition-transform active:scale-95 cursor-pointer shadow-sm"
                  title="Share on LinkedIn"
                >
                  LinkedIn
                </button>
                <button
                  type="button"
                  onClick={() => handleShare("twitter")}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-foreground text-background hover:bg-foreground/90 transition-transform active:scale-95 cursor-pointer shadow-sm"
                  title="Share on X (Twitter)"
                >
                  X (Twitter)
                </button>
                <button
                  type="button"
                  onClick={() => handleShare()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border border-border bg-card text-foreground hover:bg-accent transition-transform active:scale-95 cursor-pointer shadow-sm"
                  title="Copy link or share via device"
                >
                  {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                  <span>{copied ? "Copied Link!" : "Copy Link"}</span>
                </button>
              </div>
            </div>

            {/* Author Bio Box (E-E-A-T) */}
            <section className="author-card my-12 p-6 sm:p-8 rounded-2xl border border-border bg-card/60 backdrop-blur-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center text-primary font-bold text-xl flex-shrink-0">
                  JM
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-foreground">
                        <Link
                          to="/author/jignesh-mevada"
                          className="hover:text-primary transition-colors"
                        >
                          Written by {authorProfile.name}
                        </Link>
                      </h3>
                      <p className="text-xs text-primary font-mono uppercase tracking-wider">
                        {authorProfile.article_role}, {authorProfile.company}
                      </p>
                    </div>
                    <a
                      href={social.linkedin}
                      target="_blank"
                      rel="me noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary transition-colors border border-border rounded-lg px-2.5 py-1"
                    >
                      Connect on LinkedIn <ExternalLink size={12} />
                    </a>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {authorProfile.article_bio}
                  </p>
                </div>
              </div>
            </section>

            {/* Related Articles & Internal Links */}
            {relatedArticles.length > 0 && (
              <section className="related-articles-section border-t border-border pt-12 mt-12">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-primary">
                      KEEP READING
                    </span>
                    <h3 className="text-2xl font-bold text-foreground">
                      Related Architecture Guides
                    </h3>
                  </div>
                  <Link
                    to="/articles"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    View all <ArrowRight size={15} />
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to="/articles/$slug"
                      params={{ slug: rel.slug }}
                      className="group block p-4 rounded-xl border border-border bg-card/40 hover:bg-card hover:border-primary/40 transition-all"
                    >
                      <div className="text-xs font-mono text-muted-foreground mb-2 flex items-center justify-between">
                        <span>{rel.category}</span>
                        <span>{formatReadTime(rel.read)}</span>
                      </div>
                      <h4 className="font-semibold text-foreground text-base group-hover:text-primary transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">{rel.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>
    </SiteShell>
  );
}
